import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronRight, Copy, Image as ImageIcon, Loader2, Palette, Pencil, Plus, Shield, ShieldCheck, Type, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import Modal from '../../components/Modal/Modal'
import { QRCodeSVG } from 'qrcode.react'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import PhoneInput, { validatePhone } from '../../components/PhoneInput'
import { formatDateTimePreview } from '../../utils/format'
import { authApi } from '../../services/authApi'
import { fetchImageAsDataUrl } from '../../utils/fetchImageAsDataUrl'
import { dataUrlToSquareJpeg } from '../../utils/imageUtils'
import MediaPickerRow from '../../components/MediaPickerRow/MediaPickerRow'

const BORDER_STYLE_OPTIONS = ['solid', 'dashed', 'dotted', 'double', 'none']

const DEFAULT_FORM = {
  name: '',
  primary: '#5b21b6',
  secondary: '#3d1579',
  accent: '#d946ef',
  background: '#f9fafb',
  surface: '#ffffff',
  pageBackground: '#f9fafb',
  cardBackground: '#ffffff',
  cardBorder: '#e4e4e7',
  text: '#111827',
  textMuted: '#52525b',
  border: '#e4e4e7',
  gradientStart: '#4c1d95',
  gradientMid: '#2e1065',
  gradientEnd: '#1e0b3d',
  fontFamily: 'Inter, system-ui, sans-serif',
  headingWeight: '700',
  bodyWeight: '400',
  radiusSm: '0.5rem',
  radiusMd: '0.75rem',
  radiusLg: '1rem',
  cardBorderWidth: '1px',
  cardBorderStyle: 'solid',
  inputBorderWidth: '1px',
  buttonBorderStyle: 'solid',
  buttonBorderWidth: '1px',
  primaryButtonBorderWidth: '0px',
  menuBackgroundMode: 'gradient',
  menuSolidColor: '#4c1d95',
  menuTextColor: '#ffffff',
  menuActiveItemBg: '#ffffff',
  menuActiveItemText: '',
  buttonPrimaryMode: 'gradient',
  buttonPrimarySolidColor: '#5b21b6',
  buttonSecondaryMode: 'solid',
  buttonSecondarySolidColor: '#3d1579',
  iconBoxMode: 'light',
  iconBoxSolidColor: '#5b21b6',
  avatarShape: 'rounded',
  avatarBorderColor: '',
  avatarBorderWidth: '2px',
}

const FONT_FAMILY_OPTIONS = [
  { value: 'Inter, system-ui, sans-serif', key: 'inter' },
  { value: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', key: 'systemSafe' },
  { value: '"Segoe UI", system-ui, sans-serif', key: 'segoe' },
  { value: 'Roboto, system-ui, sans-serif', key: 'roboto' },
  { value: '"Open Sans", system-ui, sans-serif', key: 'openSans' },
  { value: 'Georgia, "Times New Roman", Times, serif', key: 'georgia' },
  { value: '"Times New Roman", Times, serif', key: 'timesNewRoman' },
  { value: 'ui-monospace, "Cascadia Code", Consolas, "Courier New", monospace', key: 'monospace' },
]

const FONT_FAMILY_VALUE_SET = new Set(FONT_FAMILY_OPTIONS.map((o) => o.value))

function normalizeFontFamily(v) {
  const s = String(v || '').trim()
  if (FONT_FAMILY_VALUE_SET.has(s)) return s
  return DEFAULT_FORM.fontFamily
}

function formFromConfigJson(configJson, initialName = '') {
  let initial = { ...DEFAULT_FORM, name: initialName }
  if (!configJson) return initial
  try {
    const parsed = typeof configJson === 'string' ? JSON.parse(configJson) : configJson
    if (!parsed) return initial
    const c = parsed.colors || {}
    const t = parsed.typography || {}
    const b = parsed.borders || {}
    const ch = parsed.chrome || {}
    initial = {
      ...initial,
      primary: c.primary || initial.primary,
      secondary: c.secondary || initial.secondary,
      accent: c.accent || initial.accent,
      background: c.background || initial.background,
      surface: c.surface || initial.surface,
      pageBackground: c.pageBackground || initial.pageBackground,
      cardBackground: c.cardBackground || initial.cardBackground,
      cardBorder: c.cardBorder || initial.cardBorder,
      text: c.text || initial.text,
      textMuted: c.textMuted || initial.textMuted,
      border: c.border || initial.border,
      gradientStart: c.gradientStart || initial.gradientStart,
      gradientMid: c.gradientMid || initial.gradientMid,
      gradientEnd: c.gradientEnd || initial.gradientEnd,
      fontFamily: normalizeFontFamily(t.fontFamily || initial.fontFamily),
      headingWeight: String(t.headingWeight ?? initial.headingWeight),
      bodyWeight: String(t.bodyWeight ?? initial.bodyWeight),
      radiusSm: b.radiusSm || initial.radiusSm,
      radiusMd: b.radiusMd || initial.radiusMd,
      radiusLg: b.radiusLg || initial.radiusLg,
      cardBorderWidth: b.cardBorderWidth || initial.cardBorderWidth,
      cardBorderStyle: b.cardBorderStyle || initial.cardBorderStyle,
      inputBorderWidth: b.inputBorderWidth || initial.inputBorderWidth,
      buttonBorderStyle: b.buttonBorderStyle || initial.buttonBorderStyle,
      buttonBorderWidth: String(b.buttonBorderWidth ?? initial.buttonBorderWidth),
      primaryButtonBorderWidth: String(b.primaryButtonBorderWidth ?? initial.primaryButtonBorderWidth),
      menuBackgroundMode: ch.menuBackgroundMode === 'solid' ? 'solid' : 'gradient',
      menuSolidColor: ch.menuSolidColor || initial.menuSolidColor,
      menuTextColor: ch.menuTextColor || initial.menuTextColor,
      menuActiveItemBg: ch.menuActiveItemBg || initial.menuActiveItemBg,
      menuActiveItemText: ch.menuActiveItemText || initial.menuActiveItemText,
      buttonPrimaryMode: ch.buttonPrimaryMode === 'solid' ? 'solid' : 'gradient',
      buttonPrimarySolidColor: ch.buttonPrimarySolidColor || initial.buttonPrimarySolidColor,
      buttonSecondaryMode: ch.buttonSecondaryMode === 'gradient' ? 'gradient' : 'solid',
      buttonSecondarySolidColor: ch.buttonSecondarySolidColor || initial.buttonSecondarySolidColor,
      iconBoxMode: ch.iconBoxMode || initial.iconBoxMode,
      iconBoxSolidColor: ch.iconBoxSolidColor || initial.iconBoxSolidColor,
      avatarShape: ch.avatarShape || initial.avatarShape,
      avatarBorderColor: ch.avatarBorderColor || initial.avatarBorderColor,
      avatarBorderWidth: ch.avatarBorderWidth || initial.avatarBorderWidth,
    }
  } catch {
    /* keep initial */
  }
  return initial
}

function buildConfigJsonFromForm(form) {
  return JSON.stringify({
    colors: {
      primary: form.primary,
      secondary: form.secondary,
      accent: form.accent,
      background: form.background,
      surface: form.surface,
      pageBackground: form.pageBackground,
      cardBackground: form.cardBackground,
      cardBorder: form.cardBorder,
      text: form.text,
      textMuted: form.textMuted,
      border: form.border,
      gradientStart: form.gradientStart,
      gradientMid: form.gradientMid,
      gradientEnd: form.gradientEnd,
    },
    typography: {
      fontFamily: form.fontFamily,
      headingWeight: Number(form.headingWeight || 700),
      bodyWeight: Number(form.bodyWeight || 400),
    },
    borders: {
      radiusSm: form.radiusSm,
      radiusMd: form.radiusMd,
      radiusLg: form.radiusLg,
      cardBorderWidth: form.cardBorderWidth,
      cardBorderStyle: form.cardBorderStyle,
      inputBorderWidth: form.inputBorderWidth,
      buttonBorderStyle: form.buttonBorderStyle,
      buttonBorderWidth: form.buttonBorderWidth,
      primaryButtonBorderWidth: form.primaryButtonBorderWidth,
    },
    chrome: {
      menuBackgroundMode: form.menuBackgroundMode,
      menuSolidColor: form.menuSolidColor,
      menuTextColor: form.menuTextColor,
      menuActiveItemBg: form.menuActiveItemBg,
      menuActiveItemText: form.menuActiveItemText,
      buttonPrimaryMode: form.buttonPrimaryMode,
      buttonPrimarySolidColor: form.buttonPrimarySolidColor,
      buttonSecondaryMode: form.buttonSecondaryMode,
      buttonSecondarySolidColor: form.buttonSecondarySolidColor,
      iconBoxMode: form.iconBoxMode,
      iconBoxSolidColor: form.iconBoxSolidColor,
      avatarShape: form.avatarShape,
      avatarBorderColor: form.avatarBorderColor,
      avatarBorderWidth: form.avatarBorderWidth,
    },
  })
}

function EditorSub({ title, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50/90 overflow-hidden">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100/60 transition-colors">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
        <h4 className="text-xs font-semibold text-gray-800 uppercase tracking-wide flex-1">{title}</h4>
      </button>
      {open && <div className="px-3 pb-3 space-y-3 border-t border-gray-200/70 pt-3">{children}</div>}
    </div>
  )
}

function SchemeAppearanceFields({ form, setForm }) {
  const { t } = useTranslation()
  const [sameRadiusEverywhere, setSameRadiusEverywhere] = useState(false)
  const applyRadiusAll = (v) => {
    setForm((p) => ({ ...p, radiusSm: v, radiusMd: v, radiusLg: v }))
  }
  const colorFields = [
    ['primary', t('settings.fields.primary')],
    ['secondary', t('settings.fields.secondary')],
    ['accent', t('settings.fields.accent')],
    ['background', t('settings.fields.background')],
    ['surface', t('settings.fields.surface')],
    ['pageBackground', t('settings.fields.pageBackground')],
    ['cardBackground', t('settings.fields.cardBackground')],
    ['cardBorder', t('settings.fields.cardBorder')],
    ['text', t('settings.fields.text')],
    ['textMuted', t('settings.fields.textMuted')],
    ['border', t('settings.fields.border')],
    ['gradientStart', t('settings.fields.gradientStart')],
    ['gradientMid', t('settings.fields.gradientMid')],
    ['gradientEnd', t('settings.fields.gradientEnd')],
  ]
  return (
    <>
      <EditorSub title={t('settings.editor.colors')} defaultOpen>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {colorFields.map(([key, label]) => (
            <label key={key} className="text-xs text-gray-600">{label}<input type="color" className="input h-10 p-1" value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} /></label>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label className="text-xs text-gray-600"><Type className="w-3 h-3 inline mr-1" />{t('settings.editor.fontFamily')}
            <select className="input mt-1" value={normalizeFontFamily(form.fontFamily)} onChange={(e) => setForm((p) => ({ ...p, fontFamily: e.target.value }))}>
              {FONT_FAMILY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{t(`settings.fontFamily.${o.key}`)}</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-600">{t('settings.editor.headingWeight')}<input type="number" min="100" max="900" step="100" className="input mt-1" value={form.headingWeight} onChange={(e) => setForm((p) => ({ ...p, headingWeight: e.target.value }))} /></label>
          <label className="text-xs text-gray-600">{t('settings.editor.bodyWeight')}<input type="number" min="100" max="900" step="100" className="input mt-1" value={form.bodyWeight} onChange={(e) => setForm((p) => ({ ...p, bodyWeight: e.target.value }))} /></label>
        </div>
      </EditorSub>

      <EditorSub title={t('settings.editor.sidebarMenu')}>
        <p className="text-[11px] text-gray-500 leading-snug">{t('settings.editor.sidebarHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">
            {t('settings.editor.backgroundFill')}
            <select className="input mt-1" value={form.menuBackgroundMode} onChange={(e) => setForm((p) => ({ ...p, menuBackgroundMode: e.target.value }))}>
              <option value="gradient">{t('settings.editor.gradient')}</option>
              <option value="solid">{t('settings.editor.solidColor')}</option>
            </select>
          </label>
          {form.menuBackgroundMode === 'solid' && (
            <label className="text-xs text-gray-600">{t('settings.editor.solidBgColor')}<input type="color" className="input h-10 p-1 mt-1" value={form.menuSolidColor} onChange={(e) => setForm((p) => ({ ...p, menuSolidColor: e.target.value }))} /></label>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs text-gray-600">{t('settings.editor.textColor')}<input type="color" className="input h-10 p-1 mt-1" value={form.menuTextColor} onChange={(e) => setForm((p) => ({ ...p, menuTextColor: e.target.value }))} /></label>
          <label className="text-xs text-gray-600">{t('settings.editor.activeItemBg')}<input type="color" className="input h-10 p-1 mt-1" value={form.menuActiveItemBg} onChange={(e) => setForm((p) => ({ ...p, menuActiveItemBg: e.target.value }))} /></label>
          <label className="text-xs text-gray-600">{t('settings.editor.activeItemText')}<input type="color" className="input h-10 p-1 mt-1" value={form.menuActiveItemText || form.primary} onChange={(e) => setForm((p) => ({ ...p, menuActiveItemText: e.target.value }))} /></label>
        </div>
      </EditorSub>

      <EditorSub title={t('settings.editor.buttons')}>
        <div className="space-y-3 border-b border-gray-200/80 pb-3">
          <p className="text-[11px] font-medium text-gray-700">{t('settings.editor.primary')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-600">{t('settings.editor.fill')}
              <select className="input mt-1" value={form.buttonPrimaryMode} onChange={(e) => setForm((p) => ({ ...p, buttonPrimaryMode: e.target.value }))}>
                <option value="gradient">{t('settings.editor.gradient')}</option><option value="solid">{t('settings.editor.solidColor')}</option>
              </select>
            </label>
            {form.buttonPrimaryMode === 'solid' && (
              <label className="text-xs text-gray-600">{t('settings.editor.solidColor')}<input type="color" className="input h-10 p-1 mt-1" value={form.buttonPrimarySolidColor} onChange={(e) => setForm((p) => ({ ...p, buttonPrimarySolidColor: e.target.value }))} /></label>
            )}
          </div>
          <button type="button" className="btn btn-ghost text-xs inline-flex items-center gap-1.5 border border-gray-200" onClick={() => setForm((p) => ({ ...p, buttonPrimaryMode: p.menuBackgroundMode, buttonPrimarySolidColor: p.menuBackgroundMode === 'solid' ? p.menuSolidColor : p.buttonPrimarySolidColor }))}>
            <Copy className="w-3.5 h-3.5" />{t('settings.editor.copyFromMenu')}
          </button>
        </div>
        <div className="space-y-3 pt-1">
          <p className="text-[11px] font-medium text-gray-700">{t('settings.editor.secondary')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-600">{t('settings.editor.fill')}
              <select className="input mt-1" value={form.buttonSecondaryMode} onChange={(e) => setForm((p) => ({ ...p, buttonSecondaryMode: e.target.value }))}>
                <option value="solid">{t('settings.editor.solidColor')}</option><option value="gradient">{t('settings.editor.gradient')}</option>
              </select>
            </label>
            {form.buttonSecondaryMode === 'solid' && (
              <label className="text-xs text-gray-600">{t('settings.editor.solidColor')}<input type="color" className="input h-10 p-1 mt-1" value={form.buttonSecondarySolidColor} onChange={(e) => setForm((p) => ({ ...p, buttonSecondarySolidColor: e.target.value }))} /></label>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-gray-200/80">
          <label className="text-xs text-gray-600">{t('settings.editor.secondaryBorderWidth')}<input className="input mt-1" value={form.buttonBorderWidth} onChange={(e) => setForm((p) => ({ ...p, buttonBorderWidth: e.target.value }))} /></label>
          <label className="text-xs text-gray-600">{t('settings.editor.primaryBorderWidth')}<input className="input mt-1" value={form.primaryButtonBorderWidth} onChange={(e) => setForm((p) => ({ ...p, primaryButtonBorderWidth: e.target.value }))} /></label>
          <label className="text-xs text-gray-600 col-span-2 sm:col-span-2">{t('settings.editor.style')}
            <select className="input mt-1" value={form.buttonBorderStyle} onChange={(e) => setForm((p) => ({ ...p, buttonBorderStyle: e.target.value }))}>
              {BORDER_STYLE_OPTIONS.map((v) => <option key={v} value={v}>{t(`settings.borderStyle.${v}`)}</option>)}
            </select>
          </label>
        </div>
      </EditorSub>

      <EditorSub title={t('settings.editor.iconBoxes')}>
        <p className="text-[11px] text-gray-500 leading-snug">{t('settings.editor.iconBoxesHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="text-xs text-gray-600">{t('settings.editor.fill')}
            <select className="input mt-1" value={form.iconBoxMode} onChange={(e) => setForm((p) => ({ ...p, iconBoxMode: e.target.value }))}>
              <option value="light">{t('settings.editor.iconBoxLight')}</option>
              <option value="gradient">{t('settings.editor.iconBoxGradient')}</option>
              <option value="solid">{t('settings.editor.solidColor')}</option>
            </select>
          </label>
          {form.iconBoxMode === 'solid' && (
            <label className="text-xs text-gray-600">{t('settings.editor.solidColor')}<input type="color" className="input h-10 p-1 mt-1" value={form.iconBoxSolidColor} onChange={(e) => setForm((p) => ({ ...p, iconBoxSolidColor: e.target.value }))} /></label>
          )}
        </div>
        {form.iconBoxMode === 'gradient' && (
          <div className="space-y-2">
            <p className="text-[11px] text-gray-500 leading-snug">{t('settings.editor.gradientStopsHint')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <label className="text-xs text-gray-600">{t('settings.editor.gradStart')}<input type="color" className="input h-10 p-1 mt-1" value={form.gradientStart} onChange={(e) => setForm((p) => ({ ...p, gradientStart: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">{t('settings.editor.gradMid')}<input type="color" className="input h-10 p-1 mt-1" value={form.gradientMid} onChange={(e) => setForm((p) => ({ ...p, gradientMid: e.target.value }))} /></label>
              <label className="text-xs text-gray-600">{t('settings.editor.gradEnd')}<input type="color" className="input h-10 p-1 mt-1" value={form.gradientEnd} onChange={(e) => setForm((p) => ({ ...p, gradientEnd: e.target.value }))} /></label>
            </div>
          </div>
        )}
        <button type="button" className="btn btn-ghost text-xs inline-flex items-center gap-1.5 border border-gray-200" onClick={() => setForm((p) => ({ ...p, iconBoxMode: p.menuBackgroundMode === 'solid' ? 'solid' : 'gradient', iconBoxSolidColor: p.menuBackgroundMode === 'solid' ? p.menuSolidColor : p.iconBoxSolidColor }))}>
          <Copy className="w-3.5 h-3.5" />{t('settings.editor.copyMenuStyle')}
        </button>
      </EditorSub>

      <EditorSub title={t('settings.editor.avatar')}>
        <p className="text-[11px] text-gray-500 leading-snug">{t('settings.editor.avatarHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label className="text-xs text-gray-600">{t('settings.editor.shape')}
            <select className="input mt-1" value={form.avatarShape} onChange={(e) => setForm((p) => ({ ...p, avatarShape: e.target.value }))}>
              <option value="rounded">{t('settings.editor.shapeRounded')}</option>
              <option value="circle">{t('settings.editor.shapeCircle')}</option>
            </select>
          </label>
          <label className="text-xs text-gray-600">{t('settings.editor.borderColor')}
            <input type="color" className="input h-10 p-1 mt-1" value={form.avatarBorderColor || form.primary} onChange={(e) => setForm((p) => ({ ...p, avatarBorderColor: e.target.value }))} />
          </label>
          <label className="text-xs text-gray-600">{t('settings.editor.borderWidth')}
            <input className="input mt-1" value={form.avatarBorderWidth} onChange={(e) => setForm((p) => ({ ...p, avatarBorderWidth: e.target.value }))} />
          </label>
        </div>
        <div className="flex items-center gap-4 pt-2 border-t border-gray-200/80">
          <div className="avatar avatar-gradient avatar-bordered w-12 h-12 text-sm font-semibold" style={form.avatarShape === 'circle' ? { borderRadius: '9999px' } : {}}>AB</div>
          <div className="avatar avatar-bordered w-12 h-12 bg-gray-200" style={{ borderColor: form.avatarBorderColor || form.primary, borderWidth: form.avatarBorderWidth || '2px', ...(form.avatarShape === 'circle' ? { borderRadius: '9999px' } : {}) }}>
            <User className="w-5 h-5 text-gray-500" />
          </div>
          <span className="text-[11px] text-gray-500">{t('settings.editor.preview')}</span>
        </div>
      </EditorSub>

      <EditorSub title={t('settings.editor.inputs')}>
        <label className="text-xs text-gray-600 block max-w-xs">{t('settings.editor.borderWidth')}<input className="input mt-1" value={form.inputBorderWidth} onChange={(e) => setForm((p) => ({ ...p, inputBorderWidth: e.target.value }))} /></label>
      </EditorSub>

      <EditorSub title={t('settings.editor.cards')}>
        {!sameRadiusEverywhere && (
          <label className="text-xs text-gray-600 block max-w-xs">{t('settings.editor.cardCornerRadius')}<input className="input mt-1" value={form.radiusLg} onChange={(e) => setForm((p) => ({ ...p, radiusLg: e.target.value }))} /></label>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <label className="text-xs text-gray-600">{t('settings.editor.borderWidth')}<input className="input mt-1" value={form.cardBorderWidth} onChange={(e) => setForm((p) => ({ ...p, cardBorderWidth: e.target.value }))} /></label>
          <label className="text-xs text-gray-600 col-span-2">{t('settings.editor.style')}
            <select className="input mt-1" value={form.cardBorderStyle} onChange={(e) => setForm((p) => ({ ...p, cardBorderStyle: e.target.value }))}>
              {BORDER_STYLE_OPTIONS.map((v) => <option key={v} value={v}>{t(`settings.borderStyle.${v}`)}</option>)}
            </select>
          </label>
        </div>
      </EditorSub>

      <EditorSub title={t('settings.editor.cornerRadii')} defaultOpen>
        <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={sameRadiusEverywhere} onChange={(e) => { const on = e.target.checked; setSameRadiusEverywhere(on); if (on) applyRadiusAll(form.radiusMd) }} />
          <span>{t('settings.editor.sameRadius')}</span>
        </label>
        {sameRadiusEverywhere ? (
          <label className="text-xs text-gray-600 block max-w-xs">{t('settings.editor.singleRadius')}<input className="input mt-1" value={form.radiusMd} onChange={(e) => applyRadiusAll(e.target.value)} /></label>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="text-xs text-gray-600">{t('settings.editor.radiusSm')}<input className="input mt-1" value={form.radiusSm} onChange={(e) => setForm((p) => ({ ...p, radiusSm: e.target.value }))} /></label>
            <label className="text-xs text-gray-600">{t('settings.editor.radiusMd')}<input className="input mt-1" value={form.radiusMd} onChange={(e) => setForm((p) => ({ ...p, radiusMd: e.target.value }))} /></label>
            <label className="text-xs text-gray-600">{t('settings.editor.radiusLg')}<input className="input mt-1" value={form.radiusLg} onChange={(e) => setForm((p) => ({ ...p, radiusLg: e.target.value }))} /></label>
          </div>
        )}
      </EditorSub>
    </>
  )
}

function parseId(v) { return v || null }

function Section({ title, icon: Icon, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center gap-2 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        {Icon && <Icon className="w-4 h-4 text-theme-secondary flex-shrink-0" />}
        <h2 className="text-lg font-semibold flex-1">{title}</h2>
        {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 space-y-4 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  )
}

function SchemeConfigPreview({ configJson }) {
  const { t } = useTranslation()
  let parsed = null
  try { parsed = typeof configJson === 'string' ? JSON.parse(configJson) : configJson } catch { /* ignore */ }
  if (!parsed) return null
  const colors = parsed.colors || {}
  const typo = parsed.typography || {}
  const brd = parsed.borders || {}
  const ch = parsed.chrome || {}
  return (
    <div className="card p-3 space-y-3 text-xs text-gray-600">
      <div>
        <span className="font-semibold text-gray-700">{t('settings.preview.properties')}</span>
        <div className="flex flex-wrap gap-1 mt-1">
          {Object.entries(colors).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1 bg-gray-50 rounded px-1.5 py-0.5 border border-gray-200">
              <span className="w-3 h-3 rounded-sm border border-gray-300" style={{ backgroundColor: v }} />
              {k}
            </span>
          ))}
        </div>
      </div>
      {typo.fontFamily && (
        <div><span className="font-semibold">{t('settings.preview.fontLabel')}:</span> {typo.fontFamily} ({t('settings.preview.fontMeta', { heading: typo.headingWeight, body: typo.bodyWeight })})</div>
      )}
      {(brd.radiusMd || brd.cardBorderStyle) && (
        <div className="space-y-1">
          <div>
            <span className="font-semibold">{t('settings.preview.radiiLabel')}:</span>{' '}
            {brd.radiusSm}/{brd.radiusMd}/{brd.radiusLg} | card {brd.cardBorderWidth} <span className="text-gray-500">({brd.cardBorderStyle || 'solid'})</span> | input {brd.inputBorderWidth}
          </div>
          <div>
            <span className="font-semibold">{t('settings.preview.buttonsLabel')}:</span> {t('settings.preview.styleLabel')} {brd.buttonBorderStyle || 'solid'} | {t('settings.preview.widthLabel')} {brd.buttonBorderWidth || '1px'} | {t('settings.preview.primaryLabel')} {brd.primaryButtonBorderWidth ?? '0px'}
          </div>
        </div>
      )}
      <div>
        <span className="font-semibold text-gray-700">{t('settings.preview.chromeTitle')}</span>
        <div className="mt-1 text-gray-600">
          {t('settings.preview.menuLabel')}: {ch.menuBackgroundMode === 'solid' ? t('settings.preview.solidLower') : t('settings.preview.gradientLower')} · {t('settings.preview.text')} {ch.menuTextColor || '#fff'}
          {' · '}{t('settings.preview.btn1')}: {ch.buttonPrimaryMode === 'solid' ? t('settings.preview.solidLower') : t('settings.preview.gradientLower')}
          {' · '}{t('settings.preview.btn2')}: {ch.buttonSecondaryMode === 'gradient' ? t('settings.preview.gradientLower') : t('settings.preview.solidLower')}
          {' · '}{t('settings.preview.iconsLabel')}: {ch.iconBoxMode || 'light'}
          {' · '}{t('settings.preview.avatarLabel')}: {ch.avatarShape || 'rounded'}
        </div>
      </div>
    </div>
  )
}

function SchemeThemeCardMockup({ configJson }) {
  const { t } = useTranslation()
  let parsed = null
  try { parsed = typeof configJson === 'string' ? JSON.parse(configJson) : configJson } catch { /* ignore */ }
  if (!parsed) return null
  const c = parsed.colors || {}
  const typo = parsed.typography || {}
  const brd = parsed.borders || {}
  const ch = parsed.chrome || {}
  const font = typo.fontFamily || 'system-ui, sans-serif'
  const wHeading = typo.headingWeight ?? 700
  const wBody = typo.bodyWeight ?? 400
  const gradStart = c.gradientStart || c.primary || '#4c1d95'
  const gradMid = c.gradientMid || c.secondary || '#2e1065'
  const gradEnd = c.gradientEnd || '#1e0b3d'
  const menuBgFinal =
    ch.menuBackgroundMode === 'solid'
      ? (ch.menuSolidColor || gradStart)
      : `linear-gradient(145deg, ${gradStart}, ${gradMid}, ${gradEnd})`
  const primaryBtnBackground =
    ch.buttonPrimaryMode === 'solid'
      ? (ch.buttonPrimarySolidColor || c.primary)
      : `linear-gradient(145deg, ${gradStart}, ${gradMid}, ${gradEnd})`
  const secSolid = ch.buttonSecondarySolidColor || c.secondary || '#3d1579'
  const secondaryBtnBackground =
    ch.buttonSecondaryMode === 'gradient'
      ? `linear-gradient(135deg, ${secSolid}, #0f0520)`
      : secSolid
  const pageBg = c.pageBackground || c.background || '#f9fafb'
  const cardBg = c.cardBackground || c.surface || '#ffffff'
  const cardBd = c.cardBorder || c.border || '#e4e4e7'
  const cardSt = brd.cardBorderStyle || 'solid'
  const btnSt = brd.buttonBorderStyle || 'solid'
  const btnW = brd.buttonBorderWidth || '1px'
  const primBdW = brd.primaryButtonBorderWidth ?? '0px'
  return (
    <div>
      <p className="text-xs font-semibold text-gray-700 mb-2">{t('settings.preview.mockupTitle')}</p>
      <div
        className="rounded-theme-lg p-4 overflow-hidden border border-dashed border-gray-300"
        style={{ backgroundColor: pageBg, fontFamily: font }}
      >
        <div className="flex gap-3 items-stretch">
          <div
            className="rounded-theme-md shrink-0 w-14 flex flex-col items-center pt-3 gap-2 shadow-sm border border-white/10"
            style={{ background: menuBgFinal, color: ch.menuTextColor || '#fff' }}
            title={t('settings.preview.sidebarMockupTitle')}
          >
            <span className="w-7 h-1.5 rounded" style={{ backgroundColor: ch.menuActiveItemBg || '#fff' }} />
            <span className="w-7 h-1.5 rounded" style={{ backgroundColor: 'currentColor', opacity: 0.25 }} />
            <span className="w-7 h-1.5 rounded" style={{ backgroundColor: 'currentColor', opacity: 0.25 }} />
          </div>
          <div
            className="flex-1 min-w-0"
            style={{
              backgroundColor: cardBg,
              color: c.text || '#111827',
              borderWidth: brd.cardBorderWidth || '1px',
              borderStyle: cardSt === 'none' ? 'none' : cardSt,
              borderColor: cardSt === 'none' ? 'transparent' : cardBd,
              borderRadius: brd.radiusLg || '1rem',
              borderLeftWidth: cardSt === 'none' ? 0 : '4px',
              borderLeftStyle: cardSt === 'none' ? 'none' : 'solid',
              borderLeftColor: c.accent || c.primary || '#5b21b6',
              padding: '1rem',
              maxWidth: '20rem',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <h4 style={{ fontWeight: wHeading, margin: 0, fontSize: '1rem', lineHeight: 1.3 }}>{t('settings.preview.cardTitle')}</h4>
            <p
              style={{
                color: c.textMuted || '#52525b',
                fontWeight: wBody,
                fontSize: '0.8125rem',
                margin: '0.5rem 0 1rem',
                lineHeight: 1.45,
              }}
            >
              {t('settings.preview.cardBody')}
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                tabIndex={-1}
                style={{
                  background: primaryBtnBackground,
                  color: '#ffffff',
                  borderStyle: btnSt === 'none' ? 'none' : btnSt,
                  borderWidth: primBdW,
                  borderColor: 'rgba(255,255,255,0.35)',
                  borderRadius: brd.radiusMd || '0.75rem',
                  padding: '0.5rem 1rem',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  cursor: 'default',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
                }}
              >
                btn-primary
              </button>
              <button
                type="button"
                tabIndex={-1}
                style={{
                  background: secondaryBtnBackground,
                  color: '#ffffff',
                  borderWidth: btnW,
                  borderStyle: btnSt === 'none' ? 'none' : btnSt,
                  borderColor: 'rgba(0,0,0,0.12)',
                  borderRadius: brd.radiusMd || '0.75rem',
                  padding: '0.5rem 0.85rem',
                  fontWeight: wBody,
                  fontSize: '0.8125rem',
                  cursor: 'default',
                }}
              >
                btn-secondary
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Settings() {
  const { t } = useTranslation()
  const { user, updateUserProfilePhoto, updateUserContact, updateUserPreferences } = useAuth()
  const { customization, isLoading, updateLogo, updateLogoMenuBackdrop, updateFavicon, createScheme, setTenantDefault, setMyTheme, updateScheme } = useTheme()
  const [logoUrl, setLogoUrl] = useState('')
  const [logoBase64, setLogoBase64] = useState('')
  const [logoMenuBackdropMode, setLogoMenuBackdropMode] = useState('transparent')
  const [logoMenuBackdropColor, setLogoMenuBackdropColor] = useState('#ffffff')
  const [faviconUrl, setFaviconUrl] = useState('')
  const [faviconBase64, setFaviconBase64] = useState('')
  const [form, setForm] = useState(DEFAULT_FORM)
  const [isCreatingScheme, setIsCreatingScheme] = useState(false)
  const [isSavingLogo, setIsSavingLogo] = useState(false)
  const [isSavingLogoBackdrop, setIsSavingLogoBackdrop] = useState(false)
  const [isSavingFavicon, setIsSavingFavicon] = useState(false)
  const [isSavingProfilePhoto, setIsSavingProfilePhoto] = useState(false)
  const [isSavingScheme, setIsSavingScheme] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [mfaStatus, setMfaStatus] = useState({ enabled: false, lastVerifiedAt: null })
  const [mfaSetup, setMfaSetup] = useState(null)
  const [mfaCode, setMfaCode] = useState('')
  const [mfaDisableModalOpen, setMfaDisableModalOpen] = useState(false)
  const [mfaDisableCode, setMfaDisableCode] = useState('')
  const [mfaConfirmError, setMfaConfirmError] = useState('')
  const [isSavingMfa, setIsSavingMfa] = useState(false)
  const [userPhotoBase64, setUserPhotoBase64] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [contactWhatsApp, setContactWhatsApp] = useState(false)
  const [contactPhoneError, setContactPhoneError] = useState('')
  const [isSavingContact, setIsSavingContact] = useState(false)
  const [dateFormat, setDateFormat] = useState('')
  const [timeFormat, setTimeFormat] = useState('')
  const [timezone, setTimezone] = useState('')
  const [isSavingPrefs, setIsSavingPrefs] = useState(false)
  const [mediaUrlModal, setMediaUrlModal] = useState(null)
  const [urlModalBusy, setUrlModalBusy] = useState(false)
  const profileFileRef = useRef(null)
  const logoFileRef = useRef(null)
  const faviconFileRef = useRef(null)
  const [tenantSchemePreviewId, setTenantSchemePreviewId] = useState(null)
  const [schemeEditor, setSchemeEditor] = useState(null)
  const [editorForm, setEditorForm] = useState(DEFAULT_FORM)
  const [isSavingEditor, setIsSavingEditor] = useState(false)

  const canManage = Boolean(customization?.canManage || customization?.CanManage)
  const defaultSchemeId = parseId(customization?.defaultSchemeId || customization?.DefaultSchemeId)
  const selectedSchemeId = parseId(customization?.selectedSchemeId || customization?.SelectedSchemeId)
  const schemes = useMemo(
    () => customization?.schemes || customization?.Schemes || [],
    [customization],
  )

  const role = user?.role || user?.Role || 'Viewer'

  useEffect(() => {
    setLogoUrl(customization?.logoUrl || customization?.LogoUrl || '')
    setLogoBase64(customization?.logoBase64 || customization?.LogoBase64 || '')
    const bdMode = customization?.logoMenuBackdropMode || customization?.LogoMenuBackdropMode || 'transparent'
    setLogoMenuBackdropMode(bdMode === 'solid' ? 'solid' : 'transparent')
    setLogoMenuBackdropColor(customization?.logoMenuBackdropColor || customization?.LogoMenuBackdropColor || '#ffffff')
    setFaviconUrl(customization?.faviconUrl || customization?.FaviconUrl || '')
    setFaviconBase64(customization?.faviconBase64 || customization?.FaviconBase64 || '')
  }, [customization])

  useEffect(() => {
    setUserPhotoBase64(user?.profilePhotoBase64 || user?.ProfilePhotoBase64 || '')
    setContactPhone(user?.phoneNumber || user?.PhoneNumber || '')
    setContactWhatsApp(user?.whatsAppAlertsEnabled ?? user?.WhatsAppAlertsEnabled ?? false)
    setDateFormat(user?.dateFormat || user?.DateFormat || '')
    setTimeFormat(user?.timeFormat || user?.TimeFormat || '')
    setTimezone(user?.timezone || user?.Timezone || '')
  }, [user])

  useEffect(() => {
    const loadMfa = async () => {
      try {
        const status = await authApi.getMfaStatus()
        setMfaStatus({
          enabled: status.enabled === true || status.Enabled === true,
          lastVerifiedAt: status.lastVerifiedAt || status.LastVerifiedAt || null,
        })
      } catch { setMfaStatus({ enabled: false, lastVerifiedAt: null }) }
    }
    loadMfa()
  }, [])

  const readFileAsDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '')
      reader.onerror = () => reject(new Error(t('settings.errors.fileRead')))
      reader.readAsDataURL(file)
    })

  const saveProfileFromDataUrl = async (dataUrl) => {
    setMessage('')
    setError('')
    setIsSavingProfilePhoto(true)
    try {
      const jpeg = await dataUrlToSquareJpeg(dataUrl, 256, 0.85)
      await authApi.updateMyProfilePhoto(jpeg)
      setUserPhotoBase64(jpeg)
      updateUserProfilePhoto(jpeg)
      setMessage(t('settings.messages.photoUpdated'))
      setMediaUrlModal(null)
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.photoSave'))
    } finally {
      setIsSavingProfilePhoto(false)
    }
  }

  const handleProfileFileAuto = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('settings.errors.invalidImage'))
      return
    }
    setError('')
    try {
      const dataUrl = await readFileAsDataUrl(file)
      await saveProfileFromDataUrl(dataUrl)
    } catch (err) {
      setError(err.message || t('settings.errors.processImage'))
    }
  }

  const handleLogoFileAuto = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('settings.errors.selectImageFile'))
      return
    }
    setMessage('')
    setError('')
    setIsSavingLogo(true)
    try {
      const dataUrl = await readFileAsDataUrl(file)
      await updateLogo({ logoUrl: null, logoBase64: dataUrl })
      setLogoBase64(dataUrl)
      setLogoUrl('')
      setMessage(t('settings.messages.logoUpdated'))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.logoSave'))
    } finally {
      setIsSavingLogo(false)
    }
  }

  const handleFaviconFileAuto = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('settings.errors.selectImageOrIco'))
      return
    }
    setMessage('')
    setError('')
    setIsSavingFavicon(true)
    try {
      const dataUrl = await readFileAsDataUrl(file)
      await updateFavicon({ faviconUrl: null, faviconBase64: dataUrl })
      setFaviconBase64(dataUrl)
      setFaviconUrl('')
      setMessage(t('settings.messages.faviconUpdated'))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.faviconSave'))
    } finally {
      setIsSavingFavicon(false)
    }
  }

  const handleClearLogo = async () => {
    setMessage('')
    setError('')
    setIsSavingLogo(true)
    try {
      await updateLogo({ logoUrl: null, logoBase64: null })
      setLogoUrl('')
      setLogoBase64('')
      setMessage(t('settings.messages.logoReset'))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.logoClear'))
    } finally {
      setIsSavingLogo(false)
    }
  }

  const persistLogoMenuBackdrop = async (mode, color) => {
    setMessage('')
    setError('')
    setIsSavingLogoBackdrop(true)
    try {
      await updateLogoMenuBackdrop({ mode, color: mode === 'solid' ? color : undefined })
      setMessage(t('settings.messages.logoBackdropUpdated'))
    } catch (err) {
      const msg = err.response?.data?.message || err.message || t('settings.errors.logoBackdrop')
      setError(msg)
      throw err
    } finally {
      setIsSavingLogoBackdrop(false)
    }
  }

  const handleLogoBackdropModeChange = async (e) => {
    const mode = e.target.value === 'solid' ? 'solid' : 'transparent'
    const prev = logoMenuBackdropMode
    setLogoMenuBackdropMode(mode)
    try { await persistLogoMenuBackdrop(mode, logoMenuBackdropColor) }
    catch { setLogoMenuBackdropMode(prev) }
  }

  const handleLogoBackdropColorChange = async (e) => {
    const c = e.target.value
    const prev = logoMenuBackdropColor
    setLogoMenuBackdropColor(c)
    if (logoMenuBackdropMode !== 'solid') return
    try { await persistLogoMenuBackdrop('solid', c) }
    catch { setLogoMenuBackdropColor(prev) }
  }

  const handleClearFavicon = async () => {
    setMessage('')
    setError('')
    setIsSavingFavicon(true)
    try {
      await updateFavicon({ faviconUrl: null, faviconBase64: null })
      setFaviconUrl('')
      setFaviconBase64('')
      setMessage(t('settings.messages.faviconReset'))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.faviconClear'))
    } finally {
      setIsSavingFavicon(false)
    }
  }

  const confirmMediaUrlModal = async () => {
    if (!mediaUrlModal) return
    const url = (mediaUrlModal.value || '').trim()
    if (!url) {
      setError(t('settings.errors.invalidUrl'))
      return
    }
    setError('')
    setUrlModalBusy(true)
    try {
      if (mediaUrlModal.kind === 'profile') {
        const dataUrl = await fetchImageAsDataUrl(url)
        await saveProfileFromDataUrl(dataUrl)
        return
      }
      if (mediaUrlModal.kind === 'logo') {
        try {
          const dataUrl = await fetchImageAsDataUrl(url)
          await updateLogo({ logoUrl: null, logoBase64: dataUrl })
          setLogoBase64(dataUrl)
          setLogoUrl('')
        } catch {
          await updateLogo({ logoUrl: url, logoBase64: null })
          setLogoUrl(url)
          setLogoBase64('')
        }
        setMessage(t('settings.messages.logoUpdated'))
        setMediaUrlModal(null)
        return
      }
      if (mediaUrlModal.kind === 'favicon') {
        try {
          const dataUrl = await fetchImageAsDataUrl(url)
          await updateFavicon({ faviconUrl: null, faviconBase64: dataUrl })
          setFaviconBase64(dataUrl)
          setFaviconUrl('')
        } catch {
          await updateFavicon({ faviconUrl: url, faviconBase64: null })
          setFaviconUrl(url)
          setFaviconBase64('')
        }
        setMessage(t('settings.messages.faviconUpdated'))
        setMediaUrlModal(null)
      }
    } catch (err) {
      setError(err.message || t('settings.errors.urlUse'))
    } finally {
      setUrlModalBusy(false)
    }
  }

  const handleCreateScheme = async (e) => {
    e.preventDefault(); setMessage(''); setError('')
    if (!form.name.trim()) { setError(t('settings.errors.schemeNameRequired')); return }
    const configJson = buildConfigJsonFromForm(form)
    setIsSavingScheme(true)
    try {
      const result = await createScheme({ name: form.name.trim(), configJson, setAsDefault: false })
      const newId = result?.id || result?.Id
      if (newId) await setMyTheme(newId)
      setForm(DEFAULT_FORM); setIsCreatingScheme(false)
      setMessage(t('settings.messages.schemeCreated'))
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.schemeCreate'))
    } finally { setIsSavingScheme(false) }
  }

  const openSchemeEditor = (scheme, mode, source) => {
    setMessage(''); setError('')
    const name = scheme.name || scheme.Name
    const cfg = scheme.configJson || scheme.ConfigJson
    const isGlobal = scheme.isGlobal === true || scheme.IsGlobal === true
    const canEditScheme = scheme.canEdit === true || scheme.CanEdit === true
    let resolvedMode = mode
    if (resolvedMode === undefined || resolvedMode === null) {
      resolvedMode = !isGlobal && canEditScheme ? 'edit' : 'copy'
    }
    const initialName = resolvedMode === 'edit' ? name : t('settings.schemeCopyPrefix', { name })
    setEditorForm(formFromConfigJson(cfg, initialName))
    setSchemeEditor({ scheme, mode: resolvedMode, source: source || 'personal' })
  }

  const closeSchemeEditor = () => {
    setSchemeEditor(null)
    setEditorForm(DEFAULT_FORM)
  }

  const handleSaveSchemeEditor = async (e) => {
    e.preventDefault()
    setMessage(''); setError('')
    if (!editorForm.name.trim()) {
      setError(t('settings.errors.schemeNameRequired'))
      return
    }
    const configJson = buildConfigJsonFromForm(editorForm)
    const se = schemeEditor
    if (!se) return
    const sid = se.scheme.id || se.scheme.Id
    const isGlobal = se.scheme.isGlobal === true || se.scheme.IsGlobal === true
    const canEditScheme = se.scheme.canEdit === true || se.scheme.CanEdit === true
    const useCopy = se.mode === 'copy' || isGlobal || !canEditScheme
    setIsSavingEditor(true)
    try {
      if (useCopy) {
        const result = await createScheme({ name: editorForm.name.trim(), configJson, setAsDefault: false })
        const newId = result?.id || result?.Id
        if (newId && se.source === 'personal') await setMyTheme(newId)
        setMessage(se.source === 'tenant' ? t('settings.messages.schemeCreatedTenant') : t('settings.messages.schemeSavedMe'))
      } else {
        await updateScheme(sid, { name: editorForm.name.trim(), configJson })
        setMessage(t('settings.messages.schemeUpdated'))
      }
      closeSchemeEditor()
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('settings.errors.schemeSave'))
    } finally {
      setIsSavingEditor(false)
    }
  }

  const handleStartMfa = async () => {
    setMessage(''); setError(''); setIsSavingMfa(true)
    try {
      const data = await authApi.startMfaSetup()
      setMfaSetup({ secretBase32: data.secretBase32 || data.SecretBase32 || '', otpAuthUri: data.otpAuthUri || data.OtpAuthUri || '' })
    } catch (err) { setError(err.message || t('settings.errors.mfaStart')) } finally { setIsSavingMfa(false) }
  }
  const handleConfirmMfa = async () => {
    setMessage('')
    setError('')
    setMfaConfirmError('')
    setIsSavingMfa(true)
    try {
      await authApi.confirmMfaSetup(mfaCode)
      setMfaStatus({ enabled: true, lastVerifiedAt: new Date().toISOString() })
      setMfaSetup(null)
      setMfaCode('')
      setMessage(t('settings.messages.mfaEnabled'))
    } catch (err) {
      setMfaConfirmError(err.message || t('settings.errors.mfaInvalidCode'))
    } finally {
      setIsSavingMfa(false)
    }
  }
  const closeMfaDisableModal = () => {
    setMfaDisableModalOpen(false)
    setMfaDisableCode('')
  }

  const handleDisableMfa = async () => {
    setMessage(''); setError('')
    if (mfaDisableCode.length !== 6) {
      setError(t('settings.errors.mfaSixDigits'))
      return
    }
    setIsSavingMfa(true)
    try {
      await authApi.disableMfa(mfaDisableCode)
      setMfaStatus({ enabled: false, lastVerifiedAt: new Date().toISOString() })
      closeMfaDisableModal()
      setMessage(t('settings.messages.mfaDisabled'))
    } catch (err) {
      setError(err.message || t('settings.errors.invalidCode'))
    } finally {
      setIsSavingMfa(false)
    }
  }

  if (isLoading) return <div className="text-gray-600">{t('settings.loadingFull')}</div>

  const tenantPreviewScheme = schemes.find((s) => (s.id || s.Id) === tenantSchemePreviewId)

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="mt-1 text-xs text-gray-500">
          {t('settings.profileLabel')}: <strong>{role}</strong>
        </p>
      </div>

      {message && <div className="card p-3 text-sm text-green-700 bg-green-50 border-green-200">{message}</div>}
      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border-red-200">{error}</div>}

      <Section title={t('settings.sectionPersonal')} icon={User} defaultOpen={true}>
        <div className="card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-800">{t('settings.profilePhotoTitle')}</h3>
          <MediaPickerRow
            title=""
            hint={t('settings.profilePhotoHint')}
            previewSrc={userPhotoBase64}
            emptyLabel={t('settings.noPhoto')}
            busy={isSavingProfilePhoto}
            fileInputRef={profileFileRef}
            accept="image/*"
            onFileChange={handleProfileFileAuto}
            onOpenUrlModal={() => { setError(''); setMediaUrlModal({ kind: 'profile', value: '' }) }}
            frameRadiusClassName="rounded-theme-md"
            previewFrameClassName="bg-[var(--theme-primary)]"
            previewClassName="w-24 h-24 rounded-theme-sm bg-gray-100"
          />
          <button
            type="button"
            className="btn btn-ghost text-xs"
            onClick={async () => {
              setIsSavingProfilePhoto(true)
              setMessage('')
              setError('')
              try {
                await authApi.updateMyProfilePhoto(null)
                setUserPhotoBase64('')
                updateUserProfilePhoto(null)
                setMessage(t('settings.messages.photoRemoved'))
              } catch (err) {
                setError(err.message || t('settings.errors.generic'))
              } finally {
                setIsSavingProfilePhoto(false)
              }
            }}
            disabled={isSavingProfilePhoto}
          >
            {t('settings.removePhoto')}
          </button>
        </div>

        <div className="card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-800">{t('settings.contactTitle')}</h3>
          <p className="text-xs text-gray-500">{t('settings.contactHint')}</p>
          <div className="max-w-sm">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.contactPhoneLabel')}</label>
            <PhoneInput
              value={contactPhone}
              onChange={(v) => { setContactPhone(v); if (contactPhoneError) setContactPhoneError('') }}
              placeholder={t('settings.contactPhonePlaceholder')}
              searchPlaceholder={t('phone.searchCountry')}
              error={contactPhoneError}
            />
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={contactWhatsApp}
              onChange={(e) => setContactWhatsApp(e.target.checked)}
              className="mt-1"
            />
            <span className="text-sm text-gray-700">{t('settings.contactWhatsAppOptIn')}</span>
          </label>
          <button
            type="button"
            className="btn btn-primary text-xs"
            disabled={isSavingContact}
            onClick={async () => {
              const perr = validatePhone(contactPhone)
              if (perr) { setContactPhoneError(t(perr)); return }
              setContactPhoneError('')
              setIsSavingContact(true)
              setMessage('')
              setError('')
              try {
                const phone = contactPhone.trim()
                const res = await authApi.updateMyContact({
                  phoneNumber: phone,
                  whatsAppAlertsEnabled: contactWhatsApp,
                })
                const savedPhone = res?.phoneNumber ?? res?.PhoneNumber ?? (phone || null)
                const savedOptIn = res?.whatsAppAlertsEnabled ?? res?.WhatsAppAlertsEnabled ?? contactWhatsApp
                setContactPhone(savedPhone || '')
                setContactWhatsApp(!!savedOptIn)
                updateUserContact({ phoneNumber: savedPhone, whatsAppAlertsEnabled: savedOptIn })
                setMessage(t('settings.messages.contactSaved'))
              } catch (err) {
                setError(err.message || t('settings.errors.generic'))
              } finally {
                setIsSavingContact(false)
              }
            }}
          >
            {isSavingContact ? t('settings.contactSaving') : t('settings.contactSave')}
          </button>
        </div>

        <div className="card p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-800">{t('settings.dateFormatTitle')}</h3>
          <p className="text-xs text-gray-500">{t('settings.dateFormatHint')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.dateFormatLabel')}</label>
              <select value={dateFormat} onChange={(e) => setDateFormat(e.target.value)} className="input w-full">
                <option value="">{t('settings.formatAuto')}</option>
                <option value="DMY">31/12/2026</option>
                <option value="MDY">12/31/2026</option>
                <option value="YMD">2026-12-31</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.timeFormatLabel')}</label>
              <select value={timeFormat} onChange={(e) => setTimeFormat(e.target.value)} className="input w-full">
                <option value="">{t('settings.formatAuto')}</option>
                <option value="H24">23:30</option>
                <option value="H12">11:30 PM</option>
              </select>
            </div>
          </div>
          <div className="max-w-md">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('settings.timezoneLabel')}</label>
            <select value={timezone} onChange={(e) => setTimezone(e.target.value)} className="input w-full">
              <option value="">{t('settings.timezoneAuto')}</option>
              <option value="America/Sao_Paulo">Brasília / São Paulo (UTC−3)</option>
              <option value="America/Noronha">Fernando de Noronha (UTC−2)</option>
              <option value="America/Belem">Belém (UTC−3)</option>
              <option value="America/Fortaleza">Fortaleza (UTC−3)</option>
              <option value="America/Recife">Recife (UTC−3)</option>
              <option value="America/Bahia">Salvador (UTC−3)</option>
              <option value="America/Campo_Grande">Campo Grande (UTC−4)</option>
              <option value="America/Cuiaba">Cuiabá (UTC−4)</option>
              <option value="America/Manaus">Manaus (UTC−4)</option>
              <option value="America/Porto_Velho">Porto Velho (UTC−4)</option>
              <option value="America/Boa_Vista">Boa Vista (UTC−4)</option>
              <option value="America/Rio_Branco">Rio Branco (UTC−5)</option>
              <option value="America/New_York">New York (UTC−5/−4)</option>
              <option value="America/Mexico_City">Cidade do México (UTC−6)</option>
              <option value="Europe/Lisbon">Lisboa (UTC+0/+1)</option>
              <option value="Europe/Madrid">Madri (UTC+1/+2)</option>
              <option value="UTC">UTC</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">{t('settings.timezoneHint')}</p>
          </div>
          <p className="text-xs text-gray-600">
            {t('settings.formatPreview')}: <span className="font-mono">{formatDateTimePreview(dateFormat, timeFormat)}</span>
          </p>
          <button
            type="button"
            className="btn btn-primary text-xs"
            disabled={isSavingPrefs}
            onClick={async () => {
              setIsSavingPrefs(true)
              setMessage('')
              setError('')
              try {
                const res = await authApi.updateMyPreferences({ dateFormat: dateFormat || '', timeFormat: timeFormat || '', timezone: timezone || '' })
                const df = res?.dateFormat ?? res?.DateFormat ?? (dateFormat || null)
                const tf = res?.timeFormat ?? res?.TimeFormat ?? (timeFormat || null)
                const tz = res?.timezone ?? res?.Timezone ?? (timezone || null)
                updateUserPreferences({ dateFormat: df, timeFormat: tf, timezone: tz })
                setMessage(t('settings.messages.prefsSaved'))
              } catch (err) {
                setError(err.message || t('settings.errors.generic'))
              } finally {
                setIsSavingPrefs(false)
              }
            }}
          >
            {isSavingPrefs ? t('settings.contactSaving') : t('settings.dateFormatSave')}
          </button>
        </div>

        <hr className="border-gray-200" />

        <h3 className="text-sm font-semibold text-gray-700">{t('settings.mySchemeTitle')}</h3>
        <p className="text-xs text-gray-500">{t('settings.mySchemeHint')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {schemes.map((scheme) => {
            const id = scheme.id || scheme.Id
            const name = scheme.name || scheme.Name
            const isDefault = id === defaultSchemeId
            const isMine = id === selectedSchemeId
            return (
              <div
                key={id}
                className={`card p-3 transition-all ${isMine ? 'scheme-card-mine' : ''} ${!isMine ? 'card-hover' : ''}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-sm">{name}</div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isDefault && <span className="badge badge-button-secondary">{t('settings.badgeTenantDefault')}</span>}
                    {isMine && <span className="badge badge-success">{t('settings.badgeMine')}</span>}
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" className="btn btn-ghost text-xs" onClick={() => setMyTheme(id)}>
                    <Check className="w-3 h-3" /> {t('settings.useForMe')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary text-xs inline-flex items-center gap-1"
                    onClick={() => openSchemeEditor(scheme, undefined, 'personal')}
                  >
                    <Pencil className="w-3 h-3" /> {t('settings.configure')}
                  </button>
                </div>
              </div>
            )
          })}
        </div>

        <hr className="border-gray-200" />

        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Shield className="w-4 h-4 text-theme-primary" />{t('settings.mfaTitle')}</h3>
        {!mfaStatus.enabled && !mfaSetup && (
          <button type="button" className="btn btn-primary inline-flex items-center justify-center gap-2" onClick={handleStartMfa} disabled={isSavingMfa}>
            <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
            {isSavingMfa ? t('settings.mfaPreparing') : t('settings.mfaEnable')}
          </button>
        )}
        {mfaSetup && (
          <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <p className="text-sm text-gray-700">{t('settings.mfaScanHint')}</p>
            <div className="flex justify-center">
              <div className="card p-3 inline-block">
                <QRCodeSVG value={mfaSetup.otpAuthUri} size={200} level="M" />
              </div>
            </div>
            <details className="text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-700">{t('settings.mfaManualSummary')}</summary>
              <div className="mt-2 break-all font-mono bg-white border border-gray-200 rounded p-2 select-all">
                {mfaSetup.secretBase32}
              </div>
            </details>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('settings.mfaCodeLabel')}
              </label>
              <input
                className="input w-full max-w-[200px] text-center text-lg font-mono tracking-widest"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isSavingMfa}
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={6}
              />
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-primary inline-flex items-center justify-center gap-2" onClick={handleConfirmMfa} disabled={isSavingMfa || mfaCode.length < 6}>
                <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
                {isSavingMfa ? t('settings.mfaVerifying') : t('settings.mfaConfirm')}
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => { setMfaSetup(null); setMfaCode('') }} disabled={isSavingMfa}>{t('common.buttons.cancel')}</button>
            </div>
          </div>
        )}
        {mfaStatus.enabled && (
          <button
            type="button"
            className="btn btn-secondary inline-flex items-center justify-center gap-2"
            onClick={() => { setError(''); setMfaDisableCode(''); setMfaDisableModalOpen(true) }}
            disabled={isSavingMfa}
          >
            <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
            {t('settings.mfaDisable')}
          </button>
        )}

        <Modal
          isOpen={Boolean(mfaConfirmError)}
          onClose={() => setMfaConfirmError('')}
          title={t('settings.mfaWrongCodeTitle')}
        >
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            {mfaConfirmError}
          </p>
          <button type="button" className="btn btn-primary" onClick={() => setMfaConfirmError('')}>
            {t('settings.gotIt')}
          </button>
        </Modal>

        <Modal isOpen={mfaDisableModalOpen} onClose={() => { if (!isSavingMfa) closeMfaDisableModal() }} title={t('settings.mfaDisableTitle')}>
          <p className="text-sm text-gray-600 mb-4">{t('settings.mfaDisablePrompt')}</p>
          <input
            className="input w-full max-w-[220px] text-center text-lg font-mono tracking-widest mb-4"
            placeholder="000000"
            value={mfaDisableCode}
            onChange={(e) => setMfaDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            disabled={isSavingMfa}
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            autoFocus
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-secondary inline-flex items-center justify-center gap-2" onClick={handleDisableMfa} disabled={isSavingMfa || mfaDisableCode.length < 6}>
              <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
              {isSavingMfa ? t('settings.mfaDisabling') : t('settings.mfaConfirmDisable')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={closeMfaDisableModal} disabled={isSavingMfa}>{t('common.buttons.cancel')}</button>
          </div>
        </Modal>
      </Section>

      {canManage && (
        <Section title={t('settings.sectionTenant')} icon={Palette} defaultOpen={false}>
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <ImageIcon className="w-4 h-4 text-theme-secondary" aria-hidden />
            {t('settings.logoFaviconTitle')}
          </h3>
          <p className="text-xs text-gray-500">{t('settings.logoFaviconHint')}</p>

          <div className="rounded-theme-lg border border-gray-200 bg-gray-50/80 p-4 space-y-6">
            <MediaPickerRow
              title={t('settings.tenantLogoTitle')}
              hint={t('settings.tenantLogoHint')}
              previewSrc={logoBase64 || logoUrl || ''}
              emptyLabel={t('settings.default')}
              busy={isSavingLogo}
              fileInputRef={logoFileRef}
              accept="image/*"
              onFileChange={handleLogoFileAuto}
              onOpenUrlModal={() => {
                setError('')
                setMediaUrlModal({ kind: 'logo', value: logoUrl || '' })
              }}
              previewClassName="w-28 h-28 rounded-theme-lg bg-white"
              previewImgClassName="object-contain"
              rowClassName="border-0 pb-0"
            />
            <div className="flex flex-wrap items-center gap-3 -mt-2">
              <button
                type="button"
                className="btn btn-ghost text-xs"
                onClick={handleClearLogo}
                disabled={isSavingLogo || (!logoBase64 && !logoUrl)}
              >
                {t('settings.resetLogo')}
              </button>
              <label className="inline-flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 text-theme-primary focus:ring-theme-primary"
                  checked={logoMenuBackdropMode === 'transparent'}
                  onChange={(e) => {
                    const synth = { target: { value: e.target.checked ? 'transparent' : 'solid' } }
                    handleLogoBackdropModeChange(synth)
                  }}
                  disabled={isSavingLogoBackdrop}
                />
                {t('settings.transparentBg')}
              </label>
              {logoMenuBackdropMode === 'solid' && (
                <input
                  type="color"
                  title={t('settings.logoBgColor')}
                  className="h-7 w-10 p-0.5 rounded border border-gray-300 cursor-pointer"
                  value={logoMenuBackdropColor}
                  onChange={handleLogoBackdropColorChange}
                  disabled={isSavingLogoBackdrop}
                />
              )}
            </div>

            <hr className="border-gray-200" />

            <MediaPickerRow
              title={t('settings.faviconTitle')}
              hint={t('settings.faviconHint')}
              previewSrc={faviconBase64 || faviconUrl || ''}
              emptyLabel={t('settings.default')}
              busy={isSavingFavicon}
              fileInputRef={faviconFileRef}
              accept="image/*,.ico"
              onFileChange={handleFaviconFileAuto}
              onOpenUrlModal={() => {
                setError('')
                setMediaUrlModal({ kind: 'favicon', value: faviconUrl || '' })
              }}
              previewClassName="w-16 h-16 rounded-theme-md bg-white"
              previewImgClassName="object-contain"
              rowClassName="border-0 pb-0"
            />
            <button
              type="button"
              className="btn btn-ghost text-xs -mt-2"
              onClick={handleClearFavicon}
              disabled={isSavingFavicon || (!faviconBase64 && !faviconUrl)}
            >
              {t('settings.resetFavicon')}
            </button>
          </div>

          <hr className="border-gray-200" />

          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-700">{t('settings.tenantSchemesTitle')}</h3>
            <button type="button" className="btn btn-primary text-xs" onClick={() => {
              const effectiveJson = customization?.effectiveSchemeConfigJson || customization?.EffectiveSchemeConfigJson
              setForm(formFromConfigJson(effectiveJson, ''))
              setIsCreatingScheme(true)
            }}>
              <Plus className="w-3 h-3" /> {t('settings.newScheme')}
            </button>
          </div>
          <p className="text-xs text-gray-500">{t('settings.tenantSchemesHint')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {schemes.map((scheme) => {
              const id = scheme.id || scheme.Id
              const name = scheme.name || scheme.Name
              const isDefault = id === defaultSchemeId
              const isOpen = id === tenantSchemePreviewId
              return (
                <div
                  key={id}
                  role="button"
                  tabIndex={0}
                  className={`card p-3 cursor-pointer transition-all ${isOpen ? 'ring-2 ring-theme-primary border-theme-primary/40' : 'card-hover'}`}
                  onClick={() => setTenantSchemePreviewId(isOpen ? null : id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      setTenantSchemePreviewId(isOpen ? null : id)
                    }
                  }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm">{name}</div>
                    {isDefault && <span className="badge badge-button-secondary flex-shrink-0">{t('settings.badgeTenantDefault')}</span>}
                  </div>
                  <div className="mt-2">
                    {!isDefault && (
                      <button
                        type="button"
                        className="btn btn-secondary text-xs"
                        onClick={(e) => {
                          e.stopPropagation()
                          setTenantDefault(id)
                        }}
                      >
                        {t('settings.setTenantDefault')}
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {tenantPreviewScheme && (
            <div className="mt-4 space-y-4 card p-4 border-theme-primary/30 bg-theme-primary/5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-800">
                  {tenantPreviewScheme.name || tenantPreviewScheme.Name}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(tenantPreviewScheme.isGlobal === true || tenantPreviewScheme.IsGlobal === true) ? (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs inline-flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation()
                        openSchemeEditor(tenantPreviewScheme, 'copy', 'tenant')
                      }}
                    >
                      <Plus className="w-3 h-3" /> {t('settings.duplicateToTenant')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary text-xs inline-flex items-center gap-1"
                      onClick={(e) => {
                        e.stopPropagation()
                        openSchemeEditor(tenantPreviewScheme, 'edit', 'tenant')
                      }}
                    >
                      <Pencil className="w-3 h-3" /> {t('settings.editScheme')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-ghost text-xs"
                    onClick={() => setTenantSchemePreviewId(null)}
                  >
                    {t('common.buttons.close')}
                  </button>
                </div>
              </div>
              <SchemeConfigPreview configJson={tenantPreviewScheme.configJson || tenantPreviewScheme.ConfigJson} />
              <SchemeThemeCardMockup configJson={tenantPreviewScheme.configJson || tenantPreviewScheme.ConfigJson} />
            </div>
          )}

          {isCreatingScheme && (
            <form className="card p-4 bg-gray-50 space-y-4 mt-2" onSubmit={handleCreateScheme}>
              <h4 className="text-sm font-semibold text-gray-700">{t('settings.createSchemeTitle')}</h4>
              <input className="input" placeholder={t('settings.schemeNamePlaceholder')} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />

              <SchemeAppearanceFields form={form} setForm={setForm} />

              <div className="flex gap-2 justify-end">
                <button type="button" className="btn btn-ghost" onClick={() => setIsCreatingScheme(false)} disabled={isSavingScheme}>{t('common.buttons.cancel')}</button>
                <button type="submit" className="btn btn-primary" disabled={isSavingScheme}>{isSavingScheme ? t('settings.creating') : t('settings.createScheme')}</button>
              </div>
            </form>
          )}
        </Section>
      )}

      <Modal
        isOpen={Boolean(mediaUrlModal)}
        onClose={() => { if (!urlModalBusy) setMediaUrlModal(null) }}
        title={
          !mediaUrlModal
            ? t('settings.urlModalDefault')
            : mediaUrlModal.kind === 'profile'
              ? t('settings.urlModalProfile')
              : mediaUrlModal.kind === 'logo'
                ? t('settings.urlModalLogo')
                : t('settings.urlModalFavicon')
        }
      >
        {mediaUrlModal && (
          <div className="space-y-4">
            <p className="text-xs text-gray-600">
              {t('settings.urlModalHint')}
            </p>
            <input
              className="input w-full"
              type="url"
              placeholder="https://…"
              value={mediaUrlModal.value}
              onChange={(e) => setMediaUrlModal((m) => (m ? { ...m, value: e.target.value } : m))}
              disabled={urlModalBusy}
              autoComplete="off"
              autoFocus
            />
            <div className="flex flex-wrap gap-2 justify-end pt-1">
              <button type="button" className="btn btn-ghost" onClick={() => setMediaUrlModal(null)} disabled={urlModalBusy}>
                {t('common.buttons.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary inline-flex items-center gap-2"
                onClick={confirmMediaUrlModal}
                disabled={urlModalBusy}
              >
                {urlModalBusy ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : null}
                {urlModalBusy ? t('settings.applying') : t('settings.apply')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={Boolean(schemeEditor)}
        onClose={() => { if (!isSavingEditor) closeSchemeEditor() }}
        title={
          schemeEditor
            ? schemeEditor.mode === 'copy'
              ? t('settings.saveSchemeCopyTitle')
              : t('settings.editSchemeTitle')
            : ''
        }
      >
        {schemeEditor && (
          <form className="space-y-4 max-h-[70vh] overflow-y-auto pr-1" onSubmit={handleSaveSchemeEditor}>
            <p className="text-xs text-gray-600">
              {schemeEditor.mode === 'copy' ? t('settings.copyModeHint') : t('settings.editModeHint')}
            </p>
            <label className="block text-xs font-medium text-gray-700">
              {t('settings.schemeNameLabel')}
              <input
                className="input mt-1"
                value={editorForm.name}
                onChange={(e) => setEditorForm((p) => ({ ...p, name: e.target.value }))}
                disabled={isSavingEditor}
              />
            </label>
            <SchemeAppearanceFields form={editorForm} setForm={setEditorForm} />
            <div className="flex flex-wrap gap-2 justify-end pt-2 border-t border-gray-100">
              <button type="button" className="btn btn-ghost" onClick={closeSchemeEditor} disabled={isSavingEditor}>
                {t('common.buttons.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSavingEditor}>
                {isSavingEditor ? t('settings.saving') : schemeEditor.mode === 'copy' ? t('settings.createScheme') : t('settings.saveChanges')}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
